import { useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import {
  ArrowLeft, Sun, Target, Heart, Users, Award,
  Zap, MapPin, Calendar, TrendingUp, ShieldCheck, Lightbulb
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const timeline = [
  {
    year: "2019",
    title: "L'Idea Nasce",
    desc: "Marco e Alessia, due ingegneri energetici di Torino, si rendono conto che trovare un installatore fotovoltaico affidabile in Italia è un incubo. Troppi preventivi gonfiati, poca trasparenza. Nasce l'idea di un servizio di comparazione onesto.",
  },
  {
    year: "2020",
    title: "Il Primo Prototipo",
    desc: "In pieno lockdown, dal garage di casa, costruiscono la prima versione della piattaforma. 12 installatori partner in Piemonte. I primi 50 clienti sono amici e parenti.",
  },
  {
    year: "2021",
    title: "Espansione al Nord",
    desc: "La voce si sparge. 200 installatori verificati nel Nord Italia. Lombardia, Veneto, Emilia-Romagna. 1.500 famiglie hanno risparmiato grazie ai nostri confronti. Il team cresce a 8 persone.",
  },
  {
    year: "2022",
    title: "Copertura Nazionale",
    desc: "Fotovoltaico Chiavi in Mano arriva in tutta Italia. 500+ installatori certificati, dal Trentino alla Sicilia. Lanciamo il configuratore intelligente che consiglia l'impianto ideale.",
  },
  {
    year: "2023",
    title: "10.000 Famiglie Solari",
    desc: "Raggiungiamo un traguardo speciale: 10.000 famiglie italiane hanno trovato il loro impianto fotovoltaico attraverso di noi. Risparmio complessivo stimato: 4,5 milioni di euro in bollette.",
  },
  {
    year: "2025",
    title: "L'Innovazione Continua",
    desc: "Lanciamo il calcolatore di risparmio in tempo reale, la verifica OTP, e il sistema webhook per CRM. Il nostro obiettivo: rendere il fotovoltaico accessibile a ogni famiglia italiana.",
  },
];

const values = [
  {
    icon: ShieldCheck,
    title: "Trasparenza Totale",
    desc: "Nessun costo nascosto, nessuna commissione al cliente. Il nostro servizio è e sarà sempre gratuito per chi cerca un impianto.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Lightbulb,
    title: "Innovazione Semplice",
    desc: "La tecnologia deve semplificare, non complicare. Ogni strumento che creiamo è pensato per essere intuitivo e immediato.",
    color: "bg-blue-50 text-blue-600",
  },
  {
    icon: Heart,
    title: "Impatto Reale",
    desc: "Non vendiamo pannelli, aiutiamo le persone a fare la scelta giusta. Ogni famiglia che passa al solare è una vittoria per tutti.",
    color: "bg-rose-50 text-rose-600",
  },
  {
    icon: Users,
    title: "Community First",
    desc: "I nostri installatori sono partner, non fornitori. Li selezioniamo, li verifichiamo e cresciamo insieme a loro.",
    color: "bg-amber-50 text-amber-600",
  },
];

const stats = [
  { value: "15.000+", label: "Famiglie aiutate", icon: Users },
  { value: "750+", label: "Installatori verificati", icon: ShieldCheck },
  { value: "20", label: "Regioni coperte", icon: MapPin },
  { value: "€8M+", label: "Risparmio generato", icon: TrendingUp },
];

const team = [
  { name: "Marco Ferretti", role: "Co-Fondatore & CEO", desc: "Ingegnere energetico, ossessionato dalla transizione green." },
  { name: "Alessia Romano", role: "Co-Fondatrice & COO", desc: "Ex consulente McKinsey, esperta di operations e scaling." },
  { name: "Davide Conti", role: "CTO", desc: "Full-stack developer, ha costruito la piattaforma dal giorno zero." },
  { name: "Giulia Marchetti", role: "Head of Partnerships", desc: "Gestisce la rete di 750+ installatori in tutta Italia." },
];

export default function ChiSiamo() {
  const navigate = useNavigate();

  useSEO({
    title: "Chi Siamo | Fotovoltaico Chiavi in Mano",
    description: "Scopri la storia di Fotovoltaico Chiavi in Mano: dal 2019 aiutiamo le famiglie italiane a trovare il miglior impianto fotovoltaico. 15.000+ famiglie, 750+ installatori.",
    canonical: `${window.location.origin}/chi-siamo`,
  });

  return (
    <div className="min-h-screen bg-white" data-testid="chi-siamo-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-14 w-auto object-contain" />
            <span className="hidden sm:block text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fotovoltaico Chiavi in Mano
            </span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 transition-colors" data-testid="chi-siamo-back">
              <ArrowLeft className="w-4 h-4" /> Torna al sito
            </button>
            <button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg shadow-emerald-500/25" data-testid="chi-siamo-cta">
              Richiedi Preventivo
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-20 sm:py-28 bg-slate-50 relative overflow-hidden" data-testid="chi-siamo-hero">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-emerald-100/40 rounded-full -translate-y-1/3 translate-x-1/3" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Chi Siamo</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="chi-siamo-title">
              Nati per rendere il<br />
              <span className="text-emerald-500">fotovoltaico</span> semplice
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-2xl">
              Siamo partiti da una frustrazione personale: perché trovare un impianto fotovoltaico affidabile in Italia
              deve essere così complicato? Da quella domanda è nata Fotovoltaico Chiavi in Mano, il servizio che mette
              in contatto chi cerca energia pulita con chi sa installarla al meglio.
            </p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-2xl">
              Non vendiamo pannelli. Non installiamo impianti. Facciamo qualcosa di più importante:
              ti aiutiamo a scegliere bene, confrontando le offerte dei migliori installatori della tua zona.
              Gratuitamente. Senza impegno. Con totale trasparenza.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-100" data-testid="chi-siamo-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
            {stats.map((s) => (
              <div key={s.label} className="text-center" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <s.icon className="w-6 h-6 text-emerald-500 mx-auto mb-3" strokeWidth={1.5} />
                <div className="text-3xl sm:text-4xl font-bold text-slate-900 stat-number" style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</div>
                <div className="text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 sm:py-28 bg-white" data-testid="chi-siamo-timeline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">La Nostra Storia</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Dal garage a tutta Italia
            </h2>
            <p className="mt-4 text-slate-500 text-base">
              Un viaggio iniziato con un'idea semplice: il fotovoltaico deve essere alla portata di tutti.
            </p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2 hidden sm:block" />

            <div className="space-y-12">
              {timeline.map((item, i) => (
                <div key={item.year} className={`relative flex flex-col sm:flex-row items-start gap-6 ${i % 2 === 0 ? "sm:flex-row" : "sm:flex-row-reverse"}`} data-testid={`timeline-${item.year}`}>
                  {/* Dot */}
                  <div className="hidden sm:flex absolute left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-emerald-500 items-center justify-center z-10 shadow-lg shadow-emerald-500/30">
                    <Calendar className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>

                  {/* Content */}
                  <div className={`sm:w-1/2 ${i % 2 === 0 ? "sm:pr-16 sm:text-right" : "sm:pl-16"}`}>
                    <span className="text-emerald-600 font-bold text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>{item.year}</span>
                    <h3 className="mt-1 text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{item.title}</h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>

                  {/* Spacer for other side */}
                  <div className="hidden sm:block sm:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28 bg-slate-50" data-testid="chi-siamo-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">I Nostri Valori</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              In cosa crediamo
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-lg transition-shadow" data-testid={`value-${v.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className={`w-12 h-12 rounded-xl ${v.color} flex items-center justify-center mb-5`}>
                  <v.icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{v.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 sm:py-28 bg-white" data-testid="chi-siamo-team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Il Team</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Le persone dietro la missione
            </h2>
            <p className="mt-4 text-slate-500 text-base">
              Un team giovane, ambizioso e appassionato di energia pulita.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((t) => (
              <div key={t.name} className="bg-slate-50 rounded-2xl p-6 hover:bg-white hover:shadow-lg hover:border-emerald-500/30 border border-transparent transition-all" data-testid={`team-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-emerald-700" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <h4 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{t.name}</h4>
                <p className="text-xs font-semibold text-emerald-600 mt-0.5">{t.role}</p>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-slate-900 relative overflow-hidden" data-testid="chi-siamo-cta-section">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Sun className="w-10 h-10 text-emerald-400 mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Pronto a passare al solare?
          </h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">
            Unisciti alle 15.000+ famiglie italiane che hanno già scelto il fotovoltaico con noi. Confronta i preventivi gratuitamente.
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-10 py-4 text-base btn-scale shadow-xl shadow-emerald-500/30 inline-flex items-center gap-2"
            data-testid="chi-siamo-final-cta"
          >
            <Zap className="w-5 h-5" />
            Configura il Tuo Impianto
          </button>
        </div>
      </section>

      {/* Mini footer */}
      <footer className="py-8 bg-slate-950 text-center">
        <p className="text-xs text-slate-500">© 2025 Fotovoltaico Chiavi in Mano. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}
